import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { ClosurePreviewLists, formatDeletionDate, type ClosurePreview } from '@/components/account/ClosurePreview';

interface ShopClosurePreview extends ClosurePreview {
  active_member_count?: number;
  staff_profiles?: ClosurePreview;
}

/**
 * Shop Profile → Admin Settings: close the shop's account (Shop ToS §10(a): "You may terminate this Agreement by
 * closing your account through the App settings").
 *
 * Core-100 R4 (OD-27): before R4 the `close-shop` function had no caller in the App. This card is rendered for owners,
 * but the authority is the server: `close_shop_account` (migration 20270206013100) refuses anyone who is not an active
 * OWNER of the shop (open legal question Q4), closes the shop, detaches every member and schedules the Privacy Policy
 * §6 deletions in one transaction. The dialog shows the server's dry run in the deletion catalogue's own words.
 */
export function CloseShopCard({ shopId, shopName, onClosed }: { shopId: string; shopName: string; onClosed?: () => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const [preview, setPreview] = useState<ShopClosurePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const canConfirm = preview !== null && confirmText === shopName && !busy;

  const open = async () => {
    setIsOpen(true);
    setBusy(true);
    setPreviewError(null);
    const { data, error } = await supabase.rpc('close_shop_account' as never, { p_shop_id: shopId, p_reason: null, p_dry_run: true } as never);
    setBusy(false);
    if (error) {
      setPreview(null);
      setPreviewError(error.message?.includes('SHOP_OWNER_REQUIRED')
        ? 'Only an owner of this shop can close it.'
        : 'What closing the shop would do could not be loaded. Nothing has changed.');
      return;
    }
    setPreview(data as unknown as ShopClosurePreview);
  };

  const reset = () => {
    setIsOpen(false);
    setPreview(null);
    setPreviewError(null);
    setReason('');
    setConfirmText('');
  };

  const confirm = async () => {
    if (!canConfirm) return;
    setBusy(true);
    const { data, error } = await supabase.rpc('close_shop_account' as never, {
      p_shop_id: shopId, p_reason: reason.trim() || null, p_dry_run: false,
    } as never);
    setBusy(false);
    if (error) {
      toast({ title: 'The shop was not closed', description: 'Nothing has changed. Please try again.', variant: 'destructive' });
      return;
    }
    const due = formatDeletionDate((data as unknown as { deletion_due_at?: string })?.deletion_due_at);
    toast({ title: 'Shop closed', description: due ? `The listed shop data will be deleted on or after ${due}.` : 'The shop is closed.' });
    reset();
    onClosed?.();
  };

  return (
    <>
      <Card className="border-destructive/30" data-testid="close-shop-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Close this shop
          </CardTitle>
          <CardDescription>
            Ends the shop&apos;s OptiBay account for everyone. You will see exactly what is deleted, and when, before you confirm.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button type="button" variant="destructive" size="sm" onClick={open} data-testid="close-shop-open">
            Close shop…
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={isOpen} onOpenChange={(o) => { if (!o) reset(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Close {shopName}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                {busy && !preview && <p className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading what this will do…</p>}
                {previewError && <p className="text-destructive" data-testid="close-shop-preview-error">{previewError}</p>}
                {preview && (
                  <>
                    <p>
                      The shop closes immediately. All {preview.active_member_count ?? 0} member(s) lose access; each
                      technician keeps their Passport.
                    </p>
                    <ClosurePreviewLists preview={preview} />
                    {preview.staff_profiles && (
                      <ClosurePreviewLists preview={preview.staff_profiles} deleteTitle="Deleted from each team member's shop profile 30 days after closing:" />
                    )}
                    <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="close-shop-reason" />
                    <p className="font-medium text-foreground">Type the shop name to confirm:</p>
                    <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={shopName} data-testid="close-shop-confirm-text" />
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <Button type="button" variant="destructive" onClick={confirm} disabled={!canConfirm} data-testid="close-shop-confirm">
              {busy && preview && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Close shop
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
